import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Polls the visitor's full list of active tickets (max 3) — sorted by
 * imminence (called > smallest position > earliest createdAt) so the UI can
 * render every ticket in the header / banners.
 *
 * Returns the current list of tickets (defaults to []). To know when the
 * first API response has landed (useful for race-free cleanup logic),
 * use `useActiveTicketsLoaded()`.
 *
 * Falls back gracefully on transient errors (keeps last known list).
 */

// Shared state (one polling loop per active mount). React renders are
// driven by re-running useState/useEffect inside each hook instance —
// the simple pattern below is enough for our needs.

export function useActiveTickets(intervalMs = 5000) {
  const { role } = useAuth();
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (role !== "client") {
        if (alive) setTickets([]);
        return;
      }
      try {
        const { data } = await api.get("/tickets/my-active-list");
        if (alive) setTickets(Array.isArray(data) ? data : []);
      } catch {
        // transient error — keep last known list
      }
    };
    load();
    const t = setInterval(load, intervalMs);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [role, intervalMs]);

  return tickets;
}

/**
 * Companion hook returning `{ tickets, loaded }`. `loaded` flips to `true`
 * after the first API response (even empty) **AND** after the auth context
 * has finished booting. Consumers that perform destructive actions (e.g.
 * cleaning up pinned-bubble state) MUST wait for `loaded === true` to
 * avoid a race during the very first paint when auth is still resolving
 * the stored token and `role` is still `null`.
 */
export function useActiveTicketsWithLoaded(intervalMs = 5000) {
  const { role, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    // Block the polling loop until the auth context has finished restoring
    // the stored token. Without this guard, the first effect run sees
    // `role=null` (auth still booting), short-circuits to setTickets([])
    // + setLoaded(true), which wipes any pinned-ticket state.
    if (authLoading) return undefined;
    const load = async () => {
      if (role !== "client") {
        if (alive) {
          setTickets([]);
          setLoaded(true);
        }
        return;
      }
      try {
        const { data } = await api.get("/tickets/my-active-list");
        if (alive) {
          setTickets(Array.isArray(data) ? data : []);
          setLoaded(true);
        }
      } catch {
        // transient error — keep last known list
      }
    };
    load();
    const t = setInterval(load, intervalMs);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [role, intervalMs, authLoading]);

  return { tickets, loaded };
}
